import { SkyDialog, SkyDropdown, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import {
  OVERRIDE_ROLES,
  ROLE_LABELS,
  ROLE_RANK,
  grantableRoles,
  type OverrideRole,
  type RoleName,
} from "@shared/permissions";
import type { AccessPerson } from "@/hooks/use-access";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";

/**
 * "Change role" on a group-enrolled person (group access, 2026-09-28; design 4.5). The person
 * keeps the group's role; an admin can raise them to Contributor or Instance admin in this
 * instance (the override), which lasts while they stay in the group, or set them back to the
 * group's role (clearing it).
 */

/** The value that clears the override. */
const GROUP_ROLE = "group";

export type ChangeRoleChoice = OverrideRole | typeof GROUP_ROLE;

export interface ChangeRoleTarget {
  person: AccessPerson;
  /** The role the group carries here. */
  groupRole: RoleName;
  groupLabel: string;
  /** The override in force now, if any. */
  current: OverrideRole | null;
}

export function ChangeRoleDialog({
  target,
  grantorRole,
  isSubmitting,
  error,
  onConfirm,
  onOpenChange,
}: {
  target: ChangeRoleTarget | null;
  grantorRole: RoleName;
  isSubmitting: boolean;
  error: InlineFailure | null;
  onConfirm: (choice: ChangeRoleChoice, target: ChangeRoleTarget) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const options = useMemo(() => {
    if (!target) return [];
    const raises = OVERRIDE_ROLES.filter(
      (role) =>
        ROLE_RANK[role] > ROLE_RANK[target.groupRole] && grantableRoles(grantorRole).includes(role),
    );
    return [
      {
        value: GROUP_ROLE,
        text: `Group role: ${ROLE_LABELS[target.groupRole]} via ${target.groupLabel}`,
        selectedText: `Group role (${ROLE_LABELS[target.groupRole]})`,
      },
      ...raises.map((role) => ({
        value: role,
        text: `${ROLE_LABELS[role]}, while in ${target.groupLabel}`,
        selectedText: ROLE_LABELS[role],
      })),
    ];
  }, [target, grantorRole]);

  const [choice, setChoice] = useState<ChangeRoleChoice>(GROUP_ROLE);
  const [shownFor, setShownFor] = useState<string | null>(target?.person.userId ?? null);
  const personId = target?.person.userId ?? null;
  if (shownFor !== personId) {
    setShownFor(personId);
    setChoice(target?.current ?? GROUP_ROLE);
  }

  const unchanged = (target?.current ?? GROUP_ROLE) === choice;

  return (
    <SkyDialog
      open={target !== null}
      onOpenChange={onOpenChange}
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="medium"
      testId="change-role-dialog"
      title={target ? `Change the role of ${target.person.email}` : "Change role"}
      description="A raised role lasts while the person stays in the group. Removed from the group, they lose it with the group's role."
      primaryAction={{
        label: "Save role",
        loading: isSubmitting,
        disabled: unchanged,
        onClick: () => {
          if (target) onConfirm(choice, target);
        },
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="space-y-4">
        <SkyDropdown
          label="Role in this instance"
          required
          testId="change-role-select"
          selectedOptions={[choice]}
          options={options}
          onOptionSelect={(values) => setChoice((values[0] as ChangeRoleChoice) ?? choice)}
        />
        <SkyText as="p" size={200} color="muted">
          Grant access directly instead when the person should keep a role outside the group.
        </SkyText>
        <InlineErrorBar error={error} title="The role was not changed" testId="change-role-error" />
      </div>
    </SkyDialog>
  );
}
